import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useViewUserFile } from '@/user_files/files/hooks/useViewUserFile'
import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import { useSourceOpensInChatPanel } from '@/drafting/workspace/hooks/useSourceOpensInChatPanel'
import { buildUserFileStandaloneViewerURL } from '@/standalone-reference-viewer/user-file/utils/build-user-file-viewer-url'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'

/**
 * Opens a file attached to a chat. In the normal chat view it opens in the content viewer. In the draft workspace it
 * opens in the chat panel's source tab when the panel can show it, and in a new browser tab when it cannot (see
 * useSourceOpensInChatPanel).
 */
export function useOpenChatAttachedFile(conversationId: string) {
  const viewUserFile = useViewUserFile()
  const { matterId } = useParams()
  const { draftArtifactVersionId } = useDraftWorkspace()
  const sourceOpensInChatPanel = useSourceOpensInChatPanel()

  return useCallback(
    (file: UserFileRead) => {
      if (draftArtifactVersionId != null && !sourceOpensInChatPanel) {
        const viewerUrl = buildUserFileStandaloneViewerURL(file.id, { matterId })
        if (viewerUrl) window.open(viewerUrl, '_blank', 'noopener,noreferrer')
        return
      }
      viewUserFile(file, { context: { kind: ViewerOpenContextKind.SELECTED_FILE, conversationId } })
    },
    [conversationId, draftArtifactVersionId, matterId, sourceOpensInChatPanel, viewUserFile]
  )
}
